import {forwardRef} from 'react';
import {FormProvider} from 'react-hook-form';
import {BrandSection, CreateBrand} from '@tinct/core-api-models';
import {Button, Card, CardContent, CardFooter} from '@tinct/web-components';
import {useCreateOrganizationBrandForm} from '../hooks/use-create-organization-brand-form';
import {createConnectedInputHelpers} from "@tinct/web-form";

/** The brand sections this form edits: its save takes these from the form, the others from the loaded version. */
const BUSINESS_PROFILE_SECTIONS: readonly BrandSection[] = ['name', 'logo', 'businessProfile'];

type UpdateBusinessProfileFormProps = {
  organizationId: string | null;
  onSuccess?: () => void;
  hideFooter?: boolean;
};

export const UpdateBusinessProfileForm = forwardRef<HTMLFormElement, UpdateBusinessProfileFormProps>(({
  hideFooter = false,
  organizationId,
  onSuccess,
}, ref) => {
  const { form, isReady, handleSubmit, isLoading } = useCreateOrganizationBrandForm({
    organizationId,
    sections: BUSINESS_PROFILE_SECTIONS,
    onSuccess,
  });

  const {ControlledImageInput, ControlledTokenInput, ControlledLanguageTokenInput, ControlledTextInput, ControlledTextareaInput} = createConnectedInputHelpers<CreateBrand>();

  // The <form> (and so the ref a funnel submits through) only exists once the
  // loaded values are in it: nothing can submit the empty defaults.
  if (!isReady) {
    return <div>Loading...</div>;
  }

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-8">
        <form ref={ref} onSubmit={handleSubmit}>
          <Card className="rounded-2xl gap-0 py-0">
            <CardContent className="px-6 pt-2">
              <div className="flex flex-col divide-y">
                <ControlledImageInput className='py-6' name="logo" label="Logo" direction='horizontal'/>
                <ControlledTextInput className='py-6' name="name" label="Brand" direction='horizontal' placeholder="Brand name..." />
                <ControlledTextInput className='py-6' name="businessProfile.industry" label="Industry" direction='horizontal' placeholder="Industry..." />
                <ControlledTextInput className='py-6' name="businessProfile.businessModel" label="Business Model" direction='horizontal' placeholder="Business model..." />
                <ControlledTextareaInput className='py-6' name="businessProfile.targetCustomer" direction='horizontal' label="Target Customer" placeholder="Add targets..." />
                <ControlledTokenInput className='py-6' name="businessProfile.geography" direction='horizontal' label='Geography' placeholder="Add geography..." />
                <ControlledLanguageTokenInput
                  className='py-6'
                  label='Main Language'
                  direction='horizontal'
                  name="businessProfile.languages"
                  placeholder="Add language..." />

              </div>
            </CardContent>
            {!hideFooter && (
              <CardFooter className="border-t justify-end py-6">
                <Button type="submit" isLoading={isLoading}>Save</Button>
              </CardFooter>
            )}
          </Card>
        </form>
      </div>
    </FormProvider>
  );
});

UpdateBusinessProfileForm.displayName = 'UpdateBusinessProfileForm';
